import React, { useEffect, useContext, useState, useMemo } from "react";
import { BASE_URL, parseWarningsAndErrors } from "../conf/config";
import { Button, Form, Input, Select, Table, notification, Tooltip, Modal, Tag, Spin, Switch, Badge } from 'antd';
import { PushpinOutlined, PushpinFilled, DeleteOutlined, DeleteFilled, DownloadOutlined } from '@ant-design/icons';
import GenerateFilePopup from "../components/GenerateFilePopup";
import { useUser } from "../UserContext";
import { checkDictOfListSanity } from "../utils/utils";
import "./Orders.css";
import { openPDFInNewTab } from "../components/GenerateFilePopup";
import { parseDate } from "../conf/config";
import { getCookie } from "../conf/config";
import { OrderContext } from "../OrdersContext";


const filterByLabelOrValue = (input, option) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) ||
    (option?.value ?? '').toLowerCase().includes(input.toLowerCase());

const isActiveConsignment = (o) => {
    const qty     = parseInt(o.quantity) || 0;
    const partial = parseInt(o.partial_delivered_quantity) || 0;
    const paid    = parseFloat(o.partially_paid) || 0;
    return o.status === 'consigned' && qty > 0 && (partial < qty || paid < qty);
};

const OrdersPage = () => {
    // Retrieve user and order data, along with utility functions
    const { orders, setOrders, refreshOrders, addNewLog, fetchWithAuth, getApiEndpoint, userGroups } = useUser();

    const {
        pinOrders, setPinOrders, searchedResult, setSearchedResult, cols, setCols,
        options, selectedRowKeys, setSelectedRowKeys,
        searchFields, setSearchFields, colFields, setColFields,
        current, setCurrent, pageSize, setPageSize, dropDownSearchItem, setDropDownSearchItem,
        statusOptions, handlePinClick, getSorter,
        GenerateFileRef,
        fieldMapping,
        fieldTooltips,
    } = useContext(OrderContext);
    const [form] = Form.useForm();
    const [filesModalOpen, setFilesModalOpen] = useState(false);
    const [orderFiles, setOrderFiles] = useState([]);
    const [filesLoading, setFilesLoading] = useState(false);
    const [filesArchive, setFilesArchive] = useState(false);

    const fetchFiles = (selected, archive) => {
        setFilesLoading(true);
        fetchWithAuth(`${getApiEndpoint()}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'files', data: selected.map(o => ({ id: o.id })), archive }),
        })
            .then(r => r.json())
            .then(data => setOrderFiles(data.files || []))
            .catch(() => notification.error({ message: 'Failed to load files' }))
            .finally(() => setFilesLoading(false));
    };

    const onSeeFiles = () => {
        const selected = orders.filter(o => selectedRowKeys.includes(o.id));
        setFilesArchive(false);
        setFilesModalOpen(true);
        fetchFiles(selected, false);
    };

    const downloadFile = async (filename) => {
        const res = await fetchWithAuth(`${window.location.origin}/media/${filename}`);
        if (!res.ok) { notification.error({ message: 'Download failed' }); return; }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); window.URL.revokeObjectURL(url);
    };


    // default values shows on the search fields and table columns headers.
    const defaultSearchItem = ["id", "status", "customer", "order_type", "order_ID"];
    const defaultColumnFields = ["id", "status", "customer", "order_type", "order_ID", "position_ID", "CPN", "shipping_date_planned", "order_date", "shipping_ID", "quantity", "fob_point", "incoterm"];


    // Order table selector
    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    // variables which need for antd table
    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        onSelectAll: (selected, selectedRows, changeRows) => {
            if (selected) {
                // if select all clicked. choose all orders
                const allOrderIds = orders.map(order => order.id);
                console.log(allOrderIds);
                setSelectedRowKeys(allOrderIds);
            } else {
                setSelectedRowKeys([]);
            }
        }
    };


    const hasSelected = selectedRowKeys.length > 0;

    // used to order select options
    function sortColFieldsAlphabetically(colFields) {
        const newColFields = [...colFields];

        newColFields.sort((a, b) => {
            let strA = String(a.label).toLowerCase();
            let strB = String(b.label).toLowerCase();

            return strA.localeCompare(strB);
        });

        return newColFields;
    }

    // Delete one column from the table
    const handleColFieldDelete = (field) => {
        const newColFields = colFields.filter(item => item.value !== field.value);
        setColFields(newColFields);
    }



    // Respond to the help window's context request with selected order info
    useEffect(() => {
        const ch = new BroadcastChannel('innocoso-help');
        ch.onmessage = (e) => {
            if (e.data?.type !== 'request-context') return;
            const selected = orders.filter(o => selectedRowKeys.includes(o.id));
            ch.postMessage({
                type: 'context',
                orders: selected.map(o => ({ id: o.id, order_ID: o.order_ID, status: o.status })),
            });
        };
        return () => ch.close();
    }, [orders, selectedRowKeys]);

    useEffect(() => {
        // if already read orders message from backend
        if (colFields.length > 0) {
            // add pin icon to the header
            const cols = [
                {
                    title: '',
                    render: (_, record) => (
                        pinOrders.some(order => order.id === record.id) ? (
                            <PushpinFilled onClick={() => handlePinClick(record)} />
                        ) : (
                            <PushpinOutlined onClick={() => handlePinClick(record)} />
                        )
                    ),
                    align: 'center',
                },
                ...colFields.map((field, index) => ({
                    title: () => (
                        <div className="table-header-column custom-header">

                            <Select
                                // options={getFilteredColItems()}
                                className="custom-selector"
                                dropdownStyle={{ width: 150 }}
                                options={sortColFieldsAlphabetically(options)}
                                value={fieldMapping[field.value] || field.value}
                                variant={false}
                                showSearch
                                filterOption={filterByLabelOrValue}
                                onClick={(e) => e.stopPropagation()}  // prevent it trigger the table row click event
                                onChange={(value) => handleColFieldChange(value, index)}
                                optionRender={(option) => (
                                    <Tooltip title={fieldTooltips[option.value]} placement="right" mouseEnterDelay={0.4}>
                                        <div style={{ width: '100%' }}>{option.label}</div>
                                    </Tooltip>
                                )}
                            />
                            <div
                                className="delete-icon-wrapper"
                                onClick={() => handleColFieldDelete(field)}
                            >
                                <DeleteOutlined className="delete-icon" />
                                <DeleteFilled className="delete-icon filled" />
                            </div>
                        </div>
                    ),
                    dataIndex: field.value,
                    key: field.value,
                    sorter: (a, b, sortOrder) => getSorter(field.value)(a, b, sortOrder),
                    sortDirections: ['ascend', 'descend'],
                    render: (text) => (
                        <div style={{ whiteSpace: 'nowrap' }}>
                            {text}
                        </div>
                    ),
                    align: 'center',
                })).filter(col => col.dataIndex)
            ];
            setCols(cols);
        }
    }, [colFields, pinOrders]);


    const addSearchField = () => {
        setSearchFields([...searchFields, getFilteredDropdownItems()[0]]);
    };

    // add field to table headers
    const addColField = () => { setColFields([...colFields, getFilteredColItems()[0]]); };

    // modify the search form if one of the header changes
    const handleFieldChange = (value, index) => {
        const newSearchFields = [...searchFields];
        newSearchFields[index] = { ...newSearchFields[index], value, label: value };
        // console.log("newSearchFields", newSearchFields);
        setSearchFields(newSearchFields);
    };

    const handleColFieldChange = (value, index) => {
        const newColFields = [...colFields];
        newColFields[index] = { ...newColFields[index], value, label: value };
        console.log("value and index", value, index);
        console.log("newColFields", newColFields);
        setColFields(newColFields);

    }

    // Get filtered dropdown items for fields
    const getFilteredDropdownItems = () => {
        return dropDownSearchItem.filter(item => !searchFields.some(field => field.label === item.label));

    };

    const getFilteredColItems = () => {
        return dropDownSearchItem.filter(item => !colFields.some(field => field.label === item.label));
    };

    // Filters columns based on values entered in the form
    const filteredColumns = (values) => {
        const filteredValues = Object.keys(values).reduce((acc, key) => {
            if (values[key] !== undefined && values[key] !== null && values[key] !== '') {
                const value_list = values[key].trim().split(";")
                    .map(value => value.trim())
                    .filter(value => value !== "");
                acc[key] = value_list;
            }
            return acc;
        }, {});

        return filteredValues;
    }

    // Search orders based on form values
    const onOrderSearch = (values) => {

        const filteredValues = filteredColumns(values);
        const checkSanity = checkDictOfListSanity(filteredValues);
        if (checkSanity !== "sane") {
            notification.warning({ message: 'warning', description: checkSanity })
            return;
        }
        const msg = { "type": "search", "data": filteredValues };

	const apiEndpoint = getApiEndpoint();
	
        fetchWithAuth(apiEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                // 'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            },
            body: JSON.stringify(msg)
        }).then((res) => {
            if (res.status === 401) {
                // unauthorized
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                notification.error({ duration: 0, message: 'Error', description: 'Unauthorized, Please login' })
                window.location.href = '/login';
                return;
            }
            return res.json();
        })

            .then((data) => {
                const { parsedWarnings, parsedErrors } = parseWarningsAndErrors(data);
                if (parsedErrors.length > 0) {
                    notification.error({ duration: 0, message: 'Error', description: parsedErrors });
                }
                if (parsedWarnings.length > 0) {
                    notification.warning({ message: 'Warning', description: parsedWarnings });
                }
                if (Array.isArray(data) && data.length === 0) {
                    notification.warning({ message: 'No results', description: 'Search returned no orders — current view unchanged.' });
                    return;
                }
                setSearchedResult(data);
            }).catch(err => console.log(err))
    }

    const boomerCounts = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return {
            overduePayments: orders.filter(o => {
                if (o.status !== 'shipped') return false;
                const due = parseDate(o.invoice_due);
                return due instanceof Date && !isNaN(due) && due < today;
            }).length,
            overdueOrders: orders.filter(o => {
                const d = parseDate(o.shipping_date_planned);
                return o.status === 'stwxe' && d instanceof Date && d < today;
            }).length,
            upcomingShipments: orders.filter(o => {
                const d = parseDate(o.shipping_date_planned);
                if (!(d instanceof Date) || isNaN(d)) return false;
                const days = (d - today) / (1000 * 60 * 60 * 24);
                return o.status === 'stwxe' && days >= 0 && days <= 7;
            }).length,
            activeConsignments: orders.filter(isActiveConsignment).length,
        };
    }, [orders]);

    // handle four types of file generation
    const onFileGenerateClick = (type) => {
        const currentDate = new Date();
        const formValues = form.getFieldsValue();
        const baseOrders = orders.filter(order =>
            Object.entries(formValues).every(([key, val]) => {
                if (!val) return true;
                return String(order[key] || '').toLowerCase().includes(String(val).toLowerCase());
            })
        );
        // fields that put to the front columns of the table
        const keyFields = ['customer', 'order_ID', 'position_ID', 'CPN', 'quantity', 'shipping_ID', 'tracking_ID', 'total_net', 'unit_price'];
        const newColFields = keyFields.map(field => ({
            label: fieldMapping[field] || field,
            value: field
        }));
        if (type === 'invoice') {
            form.setFieldsValue({ status: 'shipped' });
            form.submit();
            setColFields(newColFields);
        } else if (type === 'overdueOrders') {
            const result = baseOrders.filter((order) => {
                const shippingDatePlanned = parseDate(order.shipping_date_planned);
                return shippingDatePlanned < currentDate && order.status === "stwxe";
            });
            if (result.length === 0) {
                notification.warning({ message: 'No results', description: 'No overdue orders match the current filters.' });
                return;
            }
            setSearchedResult(result);
            const overdueOrderCols = ['id', 'status', 'customer', 'order_ID', 'position_ID', 'CPN', 'quantity', 'shipping_date_planned'];
            setColFields(overdueOrderCols.map(f => ({ value: f, label: fieldMapping[f] || f })));

        } else if (type === 'overduePayments') {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const result = baseOrders
                .filter((order) => {
                    if (order.status !== "shipped") return false;
                    const due = parseDate(order.invoice_due);
                    if (!(due instanceof Date) || isNaN(due.getTime())) return false;
                    return due < today;
                })
                .sort((a, b) => (parseDate(a.invoice_due) - parseDate(b.invoice_due)));
            if (result.length === 0) {
                notification.warning({ message: 'No results', description: 'No overdue payments match the current filters.' });
                return;
            }
            setSearchedResult(result);
            const overduePaymentCols = ['id', 'customer', 'order_ID', 'position_ID', 'invoice_ID', 'invoice_due', 'total_net', 'info'];
            setColFields(overduePaymentCols.map(f => ({ value: f, label: fieldMapping[f] || f })));

        } else if (type === 'upcomingShipments') {
            const result = baseOrders.filter((order) => {
                const shippingDatePlanned = parseDate(order.shipping_date_planned);
                const daysDifference = (shippingDatePlanned - currentDate) / (1000 * 60 * 60 * 24);
                return daysDifference >= 0 && daysDifference <= 7 && order.status === "stwxe";
            }).sort((a, b) => parseDate(a.shipping_date_planned) - parseDate(b.shipping_date_planned));
            if (result.length === 0) {
                notification.warning({ message: 'No results', description: 'No upcoming shipments match the current filters.' });
                return;
            }
            setSearchedResult(result);
            const upcomingCols = ['id', 'customer', 'order_ID', 'position_ID', 'CPN', 'quantity', 'shipping_date_planned', 'info'];
            setColFields(upcomingCols.map(f => ({ value: f, label: fieldMapping[f] || f })));

        } else if (type === 'activeConsignments') {
            const result = baseOrders.filter(isActiveConsignment).sort((a, b) => {
                const cpnCmp = (a.CPN || '').localeCompare(b.CPN || '');
                return cpnCmp !== 0 ? cpnCmp : (a.customer || '').localeCompare(b.customer || '');
            });
            if (result.length === 0) {
                notification.warning({ message: 'No results', description: 'No active consignments match the current filters.' });
                return;
            }
            setSearchedResult(result);
            const consignmentCols = ['id', 'customer', 'CPN', 'order_ID', 'position_ID', 'quantity', 'partial_delivered_quantity', 'partially_paid', 'invoice_ID'];
            setColFields(consignmentCols.map(f => ({ value: f, label: fieldMapping[f] || f })));
        }

    };


    // open modal dealing with file generation
    const onGenereteFile = (type) => {

        // Consignment workflow:
        // 1. Fresh order → Stwxe → Invoice → status: shipped → Consign → status: consigned
        //    (the "consigned" order acts as the master record for all partial deliveries)
        // 2. Each partial delivery: Invoice on the consigned order creates a NEW sub-order
        //    (position_ID gets a serial suffix, e.g. INV-001-1) and adds that sub-order's
        //    quantity to partial_delivered_quantity on the original consigned order.
        // 3. When partial_delivered_quantity >= quantity the consigned order is fully consumed
        //    and no further invoices should be created.
        if (type === 'invoice' || type === 'combine_invoice') {
            const selectedOrders = orders.filter(order => selectedRowKeys.includes(order.id));
            const fullyConsumed = selectedOrders.filter(order => {
                const qty = parseInt(order.quantity);
                const partial = parseInt(order.partial_delivered_quantity);
                return order.status === 'consigned' && !isNaN(qty) && !isNaN(partial) && qty > 0 && partial >= qty;
            });
            if (fullyConsumed.length > 0) {
                const labels = fullyConsumed.map(o => `${o.order_ID} pos. ${o.position_ID}`).join(', ');
                notification.error({
                    duration: 0,
                    message: 'Consignment fully consumed',
                    description: `The following order(s) have already been fully invoiced (partial qty = total qty): ${labels}`,
                });
                return;
            }
        }

        const invoiceOrders = orders.filter(order => selectedRowKeys.includes(order.id))
        GenerateFileRef.current.setInvoiceOrders(invoiceOrders);
        GenerateFileRef.current.setFileType(type);
        GenerateFileRef.current.open(selectedRowKeys);
    }

    const exportCSV = () => {
        const accessKey = getCookie("access_token");

        const exportOrders = orders.filter(order => selectedRowKeys.includes(order.id));
        const msg = { "type": "export", "data": exportOrders };

        fetchWithAuth(`${BASE_URL}/api/orders/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessKey}`
            },
            body: JSON.stringify(msg)
        }).then((res) => res.json())
            .then(async (data) => {
                const { parsedWarnings, parsedErrors } = parseWarningsAndErrors(data);
                addNewLog("export to csv", parsedWarnings, parsedErrors, msg);
                if (parsedErrors.length > 0) {
                    notification.error({ duration: 0, message: 'Error', description: parsedErrors });
                }
                if (parsedWarnings.length > 0) {
                    notification.warning({ message: 'Warning', description: parsedWarnings });
                }

                console.log("export to csv data", data);


                // if urls is an Array
                if (Array.isArray(data["urls"])) {
                    for (let url of data["urls"]) {
                        try {
                            const response = await fetch(url, {
                                method: 'GET',
                                headers: {
                                    'Authorization': `Bearer ${accessKey}`
                                }
                            });

                            if (!response.ok) {
                                throw new Error('Network response was not ok');
                            }

                            // tranform to blob
                            const blob = await response.blob();
                            const downloadUrl = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = downloadUrl;
                            const filename = url.split("/").pop();
                            link.download = filename;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(downloadUrl);

                        } catch (error) {
                            console.error('Error downloading file:', error);
                            notification.error({ duration: 0, message: 'Error', description: 'Failed to download file from ' + url });
                        }
                    }
                }
                // if not, only download one file
                else {
                    try {
                        const response = await fetch(data["urls"], {
                            method: 'GET',
                            headers: {
                                'Authorization': `Bearer ${accessKey}`
                            }
                        });

                        if (!response.ok)
                            throw new Error('Network response was not ok');

                        const blob = await response.blob();
                        const downloadUrl = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = downloadUrl;
                        const filename = data["urls"].split("/").pop();
                        link.download = filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(downloadUrl);

                    } catch (error) {
                        console.error('Error downloading file:', error);
                        notification.error({ duration: 0, message: 'Error', description: 'Failed to download file from ' + data["urls"] });
                    }

                }


            }).catch((error) => {
                console.error('Error:', error);
                notification.error({ duration: 0, message: 'Error', description: 'Failed to export orders' });
            });
    };


    // reset all the perseved state obtain from the context
    const resetSearch = () => {
        form.resetFields();
        const firstRecord = orders[0];
        if (typeof firstRecord !== 'object') {
            throw new TypeError('not support response type');
        } else {

            const searchItems = Object.keys(firstRecord).map((key, index) => ({
                label: key,
                key: index,
                value: key
            }));

            setDropDownSearchItem(searchItems);
            setSearchFields(defaultSearchItem.map(item => ({
                label: item,
                value: item
            })));
            setSearchedResult([]);

        }
    }


    const onMarkAsPaid = () => {
        const markedOrders = orders.filter(order => selectedRowKeys.includes(order.id));
        const msg = { "type": "payment", "data": markedOrders };
        fetchWithAuth(`${BASE_URL}/api/orders/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                // 'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            },
            body: JSON.stringify(msg)
        })
            .then(res => res.json())
            .then(data => {
                const { parsedWarnings, parsedErrors } = parseWarningsAndErrors(data);
                addNewLog("Mark orders as paid", parsedWarnings, parsedErrors, msg);
                const updatedList = data["DB"];

                if (parsedErrors.length > 0) {
                    notification.error({ duration: 0, message: 'Error', description: parsedErrors })
                }
                if (parsedWarnings.length > 0) {
                    notification.warning({ message: 'Warning', description: parsedWarnings })
                }

                // update the orders which has the same id in updated list
                setOrders(orders.map(order => updatedList.find(updated => updated.id === order.id) || order));
                refreshOrders();
                notification.success({ message: 'Success', description: 'Marked as paid successfully.' });


                if (data["detail"]) {
                    notification.error({ duration: 0, message: 'Error', description: data["detail"] });
                    return;
                }
            })
            .catch(error => console.error('Error:', error));

    };


    // simply send a test request 
    const onTest = () => {
        const markedOrders = orders.filter(order => selectedRowKeys.includes(order.id));
        const msg = { "type": "test", "data": markedOrders };
        fetchWithAuth(`${BASE_URL}/api/orders/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                // 'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            },
            body: JSON.stringify(msg)
        })
            .then(res => res.json())
            .then(data => {
                const result = data["test_result"];
                if (result) {
                    if (result.passed) {
                        notification.success({ duration: 0, message: 'Test passed', description: 'All stages passed.' });
                    } else {
                        const failures = result.stages
                            .filter(s => !s.passed)
                            .map(s => {
                                const fieldFails = s.checks
                                    .filter(c => c.result === 'fail')
                                    .map(c => `${c.key}: expected ${c.expected}, got ${c.got}`)
                                    .join('; ');
                                return `[${s.stage}] errors: ${s.errors} ${fieldFails}`;
                            })
                            .join('\n');
                        notification.error({ duration: 0, message: 'Test failed', description: failures });
                    }
                }
                if (data["detail"]) {
                    notification.error({ duration: 0, message: 'Error', description: data["detail"] });
                }
            })
            .catch(error => console.error('Error:', error));

    };


    //input for antd table
    const TableDataSource = () => {
        let dataSource = [];
        const data = Array.isArray(searchedResult) && searchedResult.length > 0 ? searchedResult : orders;
        const pinOrdersArray = Array.isArray(pinOrders) ? pinOrders : [];

        // filter out those orders already exist in pinOrders
        const filteredData = data.filter(order => !pinOrdersArray.some(pinned => pinned.id === order.id));
        dataSource = [...pinOrdersArray, ...filteredData];
        return dataSource;
    }


    return (
        <div>
            <div className="form-container">
                {/* all search fields are structured in the Form label */}
                <Form form={form} onFinish={onOrderSearch} layout="horizontal" style={{ maxWidth: 'none' }}>
                    <div className="search-form" data-help-id="search-form">
                        <Button className="search-button-group" type="primary" htmlType="submit" data-help-id="search-button">Search</Button>
                        <Button className="search-button-group" type="primary" onClick={resetSearch} danger data-help-id="reset-button">reset</Button>

                        <div className="search-field-group">
                            {searchFields.map((field, index) => (
                                <div key={index} className="search-field-container">
                                    <div className="search-form-item">
                                        <Select
                                            options={sortColFieldsAlphabetically(getFilteredDropdownItems())}
                                            onChange={(value) => handleFieldChange(value, index)}
                                            value={fieldMapping[field.value] || field.value}
                                            className="search-select custom-dropdown"
                                            dropdownStyle={{ minWidth: '150px' }}
                                            placeholder="Select Field"
                                            showSearch
                                            filterOption={filterByLabelOrValue}
                                            optionRender={(option) => (
                                                <Tooltip title={fieldTooltips[option.value]} placement="right" mouseEnterDelay={0.4}>
                                                    <div style={{ width: '100%' }}>{option.label}</div>
                                                </Tooltip>
                                            )}
                                        />

                                        {field.value === 'status' ? (
                                            //  if the field is status, directly show a select
                                            <Form.Item name={`${field.value}`} noStyle style={{ width: "100%" }}>
                                                <Select
                                                    style={{ width: "100%" }}
                                                    options={statusOptions}
                                                    className="search-select custom-dropdown"
                                                    placeholder="Select Status"
                                                />
                                            </Form.Item>
                                        ) : (
                                            <Form.Item name={`${field.value}`} noStyle>
                                                <Input className="search-input" />
                                            </Form.Item>
                                        )}
                                    </div>
                                </div>
                            ))}

                        </div>
                        <Button className="search-button-group" type="dashed" onClick={addSearchField}>Add Field</Button>
                    </div>
                </Form>
            </div>

            {/* quick search options */}
            <div className="boomer-buttons" data-help-id="boomer-buttons">
                <div>Quick Query:</div>
                <div className="tooltip-wrapper" data-tooltip="Shows all orders with status 'shipped' — ready for payment tracking.">
                <Button type="primary" onClick={() => onFileGenerateClick('invoice')}>
                    Invoices
                </Button>
                </div>
                <div className="tooltip-wrapper" data-tooltip="Shows stwxe orders whose planned shipping date has already passed.">
                <Badge count={boomerCounts.overdueOrders} color="orange">
                <Button type="primary" onClick={() => onFileGenerateClick('overdueOrders')}>
                    Overdue Orders
                </Button>
                </Badge>
                </div>
                <div className="tooltip-wrapper" data-tooltip="Shows shipped orders whose payment due date has passed, sorted by due date.">
                <Badge count={boomerCounts.overduePayments}>
                <Button type="primary" onClick={() => onFileGenerateClick('overduePayments')}>
                    Overdue Payments
                </Button>
                </Badge>
                </div>
                <div className="tooltip-wrapper" data-tooltip="Shows stwxe orders with planned shipping within the next 7 days, sorted by date.">
                <Badge count={boomerCounts.upcomingShipments} color="blue">
                <Button type="primary" onClick={() => onFileGenerateClick('upcomingShipments')}>
                    Upcoming shipments
                </Button>
                </Badge>
                </div>
                <div className="tooltip-wrapper" data-tooltip="Shows consigned orders with remaining quantity still to be delivered.">
                <Badge count={boomerCounts.activeConsignments} color="green">
                <Button type="primary" onClick={() => onFileGenerateClick('activeConsignments')}>
                    Active Consignments
                </Button>
                </Badge>
                </div>
            </div>


            <div className="main-container">
                <div className="table-control-buttons" data-help-id="add-column-button">
                    <Button onClick={addColField} type="primary">Add Table Column</Button>
                </div>
                <div className="table-content">
                    <div className="generate-file-buttons" data-help-id="action-panel">

                        {/* --- Setup --- */}
                        {userGroups.includes("admin") && (
                        <div className="tooltip-wrapper" data-tooltip="Directly edit field values on the selected order(s). No status change, no document generated." data-help-id="edit-button">
                        <Button onClick={() => onGenereteFile("edit")} type="primary" danger disabled={!hasSelected}>
                            Edit
                        </Button>
                        </div>
                        )}
                        <div className="tooltip-wrapper" data-tooltip="Creates a new order as a sub-position of the selected order. The new order inherits fields from the original, starts with status stwxe, and gets a new position ID (original + '.1'). No PDF." data-help-id="sub-position-button">
                        <Button onClick={() => onGenereteFile("add_sub_position")} type="primary" style={{ backgroundColor: '#faad14' }} disabled={!hasSelected}>
                            Add Sub-Position
                        </Button>
                        </div>

                        <hr />

                        {/* --- Documents --- */}
                        {userGroups.includes("admin") && (
                        <div className="tooltip-wrapper" data-tooltip="Generates an order confirmation PDF without advancing the order status. Groups PDF by order ID." data-help-id="confirmation-button">
                        <Button onClick={() => onGenereteFile("confirmation")} type="primary" disabled={!hasSelected}>
                            Confirmation
                        </Button>
                        </div>
                        )}
                        {userGroups.includes("admin") && (
                        <div className="tooltip-wrapper" data-tooltip="Advances a fresh order to stwxe status and generates the Stwxe export document. Auto-assigns an inno ID if empty. Groups PDF by inno ID. Requires shipping ID, tracking ID, batches & Stwxe invoice ID." data-help-id="stwxe-button">
                        <Button onClick={() => onGenereteFile("stwxe_order")} type="primary" disabled={!hasSelected}>
                            Stwxe
                        </Button>
                        </div>
                        )}
                        {userGroups.includes("admin") && (
                        <div className="tooltip-wrapper" data-tooltip="Generates invoice PDF, sets status → shipped. Auto-assigns invoice ID if empty. Requires shipping ID & tracking ID. Groups PDF by invoice ID. If order is invoice_cancelled, creates a replacement order. If consigned, creates a new sub-position order." data-help-id="invoice-button">
                        <Button onClick={() => onGenereteFile("invoice")} type="primary" disabled={!hasSelected}>
                            Invoice
                        </Button>
                        </div>
                        )}
                        {userGroups.includes("admin") && (
                        <div className="tooltip-wrapper" data-tooltip="Generates a pre-shipment invoice PDF without changing order status or requiring shipping details. Groups PDF by invoice ID." data-help-id="proforma-button">
                        <Button onClick={() => onGenereteFile("proforma_invoice")} type="primary" disabled={!hasSelected}>
                            Proforma Invoice
                        </Button>
                        </div>
                        )}
                        {userGroups.includes("admin") && (
                        <div className="tooltip-wrapper" data-tooltip="Generates an Excel import package (contract, packlist, invoice, overview sheets) for China customs. Requires invoice ID." data-help-id="china-import-button">
                        <Button onClick={() => onGenereteFile("china_import")} type="primary" disabled={!hasSelected}>
                            China Import
                        </Button>
                        </div>
                        )}
                        <div className="tooltip-wrapper" data-tooltip="Generates an Excel and PDF shipping note. Groups PDF by shipping ID. Requires shipping ID, tracking ID, batches & Stwxe invoice ID." data-help-id="shipping-note-button">
                        <Button onClick={() => onGenereteFile("shipping_note")} type="primary" disabled={!hasSelected}>
                            Shipping Note
                        </Button>
                        </div>
                        {userGroups.includes("admin") && (
                        <div className="tooltip-wrapper" data-tooltip="For a consigned order: invoices the current partial delivery by creating a new sub-position order and updates the partial delivered quantity on the original. The new order is marked consigned for further deliveries. No PDF generated." data-help-id="consign-button">
                        <Button onClick={() => onGenereteFile("consign")} type="primary" disabled={!hasSelected}>
                            Consign
                        </Button>
                        </div>
                        )}

                        <hr />

                        {/* --- Combined docs --- */}
                        {userGroups.includes("admin") && (
                        <div className="tooltip-wrapper" data-tooltip="Same as Invoice, but merges all selected orders into one PDF. Set invoice ID to 'combined' to auto-group under a shared invoice ID." data-help-id="combine-invoice-button">
                        <Button onClick={() => onGenereteFile("combine_invoice")} type="primary" disabled={!hasSelected}>
                            Combine Invoice
                        </Button>
                        </div>
                        )}
                        {userGroups.includes("admin") && (
                        <div className="tooltip-wrapper" data-tooltip="Same as Stwxe, but merges all selected orders into one document. Set inno ID to 'combined' to auto-group." data-help-id="combine-stwxe-button">
                        <Button onClick={() => onGenereteFile("combine_stwxe")} type="primary" disabled={!hasSelected}>
                            Combine Stwxe
                        </Button>
                        </div>
                        )}
                        {userGroups.includes("admin") && (
                        <div className="tooltip-wrapper" data-tooltip="Same as Confirmation, but merges all selected orders into one PDF grouped by order ID." data-help-id="combine-confirmation-button">
                        <Button onClick={() => onGenereteFile("combine_confirmation")} type="primary" disabled={!hasSelected}>
                            Combine Confirmation
                        </Button>
                        </div>
                        )}

                        <hr />

                        {/* --- Resolution --- */}
                        {userGroups.includes("admin") && (
                        <div className="tooltip-wrapper" data-tooltip="Creates a new credit order (negative-value copy of the original) and generates a credit note PDF grouped by credit ID. If the order already has status 'credit', overwrites the existing credit entry." data-help-id="credit-note-button">
                        <Button onClick={() => onGenereteFile("credit_note")} type="primary" danger disabled={!hasSelected}>
                            Credit Note
                        </Button>
                        </div>
                        )}
                        {userGroups.includes("admin") && (
                        <div className="tooltip-wrapper" data-tooltip="Sets status → invoice_cancelled, auto-assigns a cancellation ID, generates a cancellation PDF grouped by cancellation ID. To re-invoice afterwards, change status back to shipped and use Invoice." data-help-id="cancel-invoice-button">
                        <Button type="primary" danger onClick={() => onGenereteFile("invoice_cancel")} disabled={!hasSelected}>
                            Cancel Invoice
                        </Button>
                        </div>
                        )}
                        <div className="tooltip-wrapper" data-tooltip="Cancels the order: sets status → cancelled, quantity → 0, clears shipping/tracking/invoice IDs. No document generated." data-help-id="cancel-order-button">
                        <Button onClick={() => onGenereteFile("cancel")} type="primary" danger disabled={!hasSelected}>
                            Cancel Order
                        </Button>
                        </div>
                        {userGroups.includes("admin") && (
                        <div className="tooltip-wrapper" data-tooltip="Records the payment date on the selected order(s)." data-help-id="mark-paid-button">
                        <Button type="primary" style={{ backgroundColor: '#52c41a' }} onClick={onMarkAsPaid} disabled={!hasSelected}>
                            Mark as Paid
                        </Button>
                        </div>
                        )}

                        <hr />

                        {/* --- Utility --- */}
                        {userGroups.includes("admin") && (
                        <div className="tooltip-wrapper" data-tooltip="Downloads selected orders as a CSV file. No DB changes.">
                        <Button onClick={exportCSV} type="primary" style={{ backgroundColor: 'silver' }} disabled={!hasSelected}>
                            Export CSV
                        </Button>
                        </div>
                        )}
                        <div className="tooltip-wrapper" data-tooltip="Show all generated files linked to the selected order(s).">
                        <Button type="primary" style={{ backgroundColor: 'silver' }} onClick={onSeeFiles} disabled={!hasSelected}>
                            See Files
                        </Button>
                        </div>

                    </div>
                    <div className="table-container">
                        <Table
                            className="order-table"
                            rowSelection={rowSelection}
                            dataSource={TableDataSource()}
                            scroll={{ x: 'max-content' }}
                            pagination={{
                                current: current,
                                pageSize: pageSize,
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50', '100', '200'],
                                onChange: (page, size) => {
                                    setCurrent(page);
                                    setPageSize(size);
                                }
                            }}
                            columns={cols}
                            rowKey="id"
                        />

                    </div>
                </div>
            </div>
            <GenerateFilePopup ref={GenerateFileRef} />
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span>Files</span>
                        <Switch
                            size="small"
                            checked={filesArchive}
                            onChange={v => {
                                setFilesArchive(v);
                                fetchFiles(orders.filter(o => selectedRowKeys.includes(o.id)), v);
                            }}
                        />
                        <span style={{ fontSize: 12, color: '#888' }}>Show archive</span>
                    </div>
                }
                open={filesModalOpen}
                onCancel={() => setFilesModalOpen(false)}
                footer={null}
                width={800}
            >
                {filesLoading ? (
                    <Spin style={{ display: 'block', margin: '40px auto' }} />
                ) : orderFiles.length === 0 ? (
                    <p style={{ color: '#888' }}>No files found for the selected order(s).</p>
                ) : (
                    <Table
                        size="small"
                        pagination={false}
                        rowKey="id"
                        dataSource={orderFiles}
                        columns={[
                            {
                                title: 'Type',
                                dataIndex: 'file_type',
                                width: 55,
                                render: t => <Tag color={t === 'pdf' ? 'red' : 'green'}>{t.toUpperCase()}</Tag>,
                            },
                            {
                                title: 'Doc',
                                dataIndex: 'doc_type',
                                width: 120,
                                render: (dt, row) => (
                                    <span style={{ fontSize: 12 }}>{dt}<br />
                                    <span style={{ color: '#888', fontSize: 11 }}>{row.document_id}</span></span>
                                ),
                            },
                            {
                                title: 'Order / Position',
                                dataIndex: 'order_info',
                                width: 130,
                                render: info => info?.map((o, i) => (
                                    <div key={i} style={{ fontSize: 11, lineHeight: '1.4' }}>
                                        {o.order_ID} / {o.position_ID}
                                    </div>
                                )),
                            },
                            {
                                title: 'Shipping ID',
                                dataIndex: 'order_info',
                                key: 'shipping',
                                width: 110,
                                render: info => [...new Set(info?.map(o => o.shipping_ID).filter(Boolean))].map((s, i) => (
                                    <div key={i} style={{ fontSize: 11 }}>{s}</div>
                                )),
                            },
                            {
                                title: 'By',
                                dataIndex: 'created_by',
                                width: 50,
                                render: v => <span style={{ fontSize: 11 }}>{v}</span>,
                            },
                            {
                                title: 'Date',
                                dataIndex: 'created_at',
                                width: 85,
                                render: d => <span style={{ fontSize: 11 }}>{d.split('T')[0]}</span>,
                            },
                            {
                                title: '',
                                width: 36,
                                render: (_, row) => (
                                    <Tooltip title={row.filename} placement="left">
                                        <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadFile(row.filename)} />
                                    </Tooltip>
                                ),
                            },
                        ]}
                    />
                )}
            </Modal>
        </div>
    );
}

export default OrdersPage;


